import {
  IconBrandInstagram,
  IconBrandYoutube,
  IconMinus,
  IconPlus,
} from "@tabler/icons-react";
import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logoSvg from "../assets/logo.svg";
import { useAuth } from "../context/AuthContext";
import { isLandingRoute } from "../utils/landingRoutes";
import styles from "./Footer.module.css";
import FooterEmailBanner from "./FooterEmailBanner";

export default function Footer() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { user, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const inLandingFlow = (() => {
    try {
      const m = sessionStorage.getItem("auraMode");
      return m === "market" || m === "global";
    } catch {
      return false;
    }
  })();
  // Hide old footer (show new CTA only) on /market & /global, and on /reports
  // when in either landing mode.
  // Landing pages (incl. the SEO ones) show the CTA banner instead of the
  // full sitemap - see utils/landingRoutes.
  const isMarket =
    isLandingRoute(location.pathname) ||
    (inLandingFlow && location.pathname.startsWith("/reports"));

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleProtectedLinkClick = (path: string) => {
    if (user) {
      navigate(path);
    } else {
      signInWithGoogle();
    }
  };

  // `seo` overrides the crawlable href on protected links. The click still opens
  // the auth flow and lands on `path`, so behaviour is unchanged, but the
  // anchor a crawler follows now points at the public landing page instead of
  // /chat or /reports. Those two are noindex (see IndexPolicy) because Amplify
  // serves them a copy of the homepage; 13 of the footer's 33 links were
  // pointing there, which is how they got indexed and why the real landing
  // pages were starved of internal links.
  const accordionData = [
    {
      title: "Core Features",
      links: [
        { text: "Home", path: "/", protected: false },
        { text: "AI Astrologer", path: "/ai-astrologer", protected: false },
        // Sitewide anchor for the new Hinglish jyotish page (item 23).
        { text: "AI Jyotish Free Chat", path: "/ai-jyotish", protected: false },
        {
          text: "Birth Chart",
          path: "/reports",
          seo: "/free-kundli",
          protected: true,
        },
        {
          text: "Daily Predictions",
          path: "/chat",
          seo: "/daily-horoscope",
          protected: true,
        },
        {
          text: "Compatibility",
          path: "/reports",
          seo: "/kundli-matching",
          protected: true,
        },
        {
          text: "Remedies",
          path: "/chat",
          seo: "/ai-astrologer",
          protected: true,
        },
        {
          text: "AI Chat",
          path: "/chat",
          seo: "/ai-astrologer",
          protected: true,
        },
      ],
    },
    {
      // Renamed from "Astrology Tools" to carry the term people actually
      // search. /panchang and /muhurat were prerendered and indexable with
      // ZERO internal links from anywhere on the site, the same orphan problem
      // /global had. The footer is the one place that can link every page from
      // every page, so they belong here.
      title: "Free Calculators",
      links: [
        { text: "Manglik Calculator", path: "/manglik-calculator", protected: false },
        { text: "Kaal Sarp Dosha", path: "/kaal-sarp-dosha", protected: false },
        { text: "Sade Sati Calculator", path: "/sade-sati-calculator", protected: false },
        { text: "Moon Sign Calculator", path: "/moon-sign-calculator", protected: false },
        { text: "Nakshatra Calculator", path: "/nakshatra-calculator", protected: false },
        { text: "Rising Sign Calculator", path: "/rising-sign-calculator", protected: false },
        { text: "Mahadasha Calculator", path: "/mahadasha-calculator", protected: false },
        { text: "AI Kundli Free", path: "/free-kundli", protected: false },
        { text: "Kundli Matching", path: "/kundli-matching", protected: false },
      ],
    },
    {
      // Splitting a 17-link column. It set the height of the whole footer row,
      // so every column after it wrapped against a wall of empty space. These
      // read today's sky, or are consult pages, rather than chart calculators.
      title: "Daily & Consult",
      links: [
        // Anchor matches the retargeted cluster head (item 17): the page now
        // owns "AI Horoscope Free"; /horoscope/ keeps the Today anchor below.
        { text: "AI Horoscope Free", path: "/daily-horoscope", protected: false },
        { text: "Today's Panchang", path: "/panchang", protected: false },
        { text: "Shubh Muhurat", path: "/muhurat", protected: false },
        {
          // Anchor is the cluster head term, not "Talk to Astrologer": GSC
          // 2026-10-12 had /talk-to-astrologer at position 81-95 for every
          // "free astrology chat" query while the homepage held ~7. Google
          // needs a sitewide signal that THIS page owns the chat intent.
          text: "Free Astrology Chat",
          path: "/talk-to-astrologer",
          protected: false,
        },
        {
          text: "Astrologer in Dubai",
          path: "/vedic-astrologer-dubai",
          protected: false,
        },
        {
          text: "Indian Astrologer in USA",
          path: "/indian-astrologer-usa",
          protected: false,
        },
        // /global sat in the sitemap with ZERO internal links from anywhere on
        // the site: a genuine orphan, and it is the page used for international
        // markets.
        {
          text: "Astrology Worldwide",
          path: "/global",
          protected: false,
        },
      ],
    },
    {
      /* Every page here was in the sitemap with zero inbound internal links
         (GSC: zero impressions each). Keyworded anchors, and the slash paths
         on purpose: the bare forms 301 to these (except /usa and /uae, see
         below). /uae /love /horoscope are geo-gated, an Indian tap lands on
         the homepage, which is the gate's intended behaviour; Google still
         needs the links. /usa is ungated since USA batch 1. */
      title: "Astrology Guides",
      links: [
        // Anchor matches the retargeted head term (item 21): the page now
        // owns "Career Astrology AI".
        { text: "Career Astrology AI", path: "/career/", protected: false },
        // Anchor matches the retargeted head term (item 22): the page now
        // owns "Love Astrology Free Chat".
        { text: "Love Astrology Free Chat", path: "/love-astrology/", protected: false },
        // Anchor matches the retargeted head term (item 20): the page now
        // owns "AI Marriage Prediction".
        { text: "AI Marriage Prediction", path: "/marriage/", protected: false },
        {
          text: "Thirumana Porutham",
          path: "/thirumana-porutham/",
          protected: false,
        },
        { text: "AI Astrology India", path: "/india/", protected: false },
        /* Bare paths for /usa and /uae on purpose: unlike the other geo
           pages, their slash forms are the ones that 301 (verified live
           2026-10-21). /usa is the NRI hub as of USA batch 1. */
        { text: "Vedic Astrology USA", path: "/usa", protected: false },
        { text: "AI Astrology UAE", path: "/uae", protected: false },
        { text: "Love Compatibility AI", path: "/love/", protected: false },
        { text: "AI Horoscope Today", path: "/horoscope/", protected: false },
      ],
    },
    {
      title: "Astrology Blog",
      links: [
        { text: "All Articles", path: "/blog", protected: false },
        { text: "Fire Signs Explained", path: "/blog/fire-signs-in-vedic-astrology", protected: false },
        { text: "Full Moon Guide", path: "/blog/full-moon-purnima-astrology", protected: false },
        {
          text: "Planetary Transits",
          path: "/blog/planetary-transits-explained",
          protected: false,
        },
      ],
    },
    {
      title: "Insights",
      links: [
        {
          text: "Career Guidance",
          path: "/chat",
          seo: "/talk-to-astrologer",
          protected: true,
        },
        {
          text: "Love Life",
          path: "/reports",
          seo: "/kundli-matching",
          protected: true,
        },
        {
          text: "Health Insights",
          path: "/reports",
          seo: "/ai-astrologer",
          protected: true,
        },
        {
          text: "Financial Forecast",
          path: "/reports",
          seo: "/daily-horoscope",
          protected: true,
        },
        {
          text: "Spiritual Growth",
          path: "/reports",
          seo: "/talk-to-astrologer",
          protected: true,
        },
        {
          text: "Life Path",
          path: "/reports",
          seo: "/free-kundli",
          protected: true,
        },
      ],
    },
    {
      title: "Support",
      links: [
        { text: "About Us", path: "/about", protected: false },
        { text: "How It Works", path: "/how-it-works", protected: false },
        { text: "Our Methodology", path: "/methodology", protected: false },
        { text: "Free Astrology Tools", path: "/free-astrology", protected: false },
        { text: "हिंदी में ज्योतिष", path: "/hi", protected: false },
        { text: "Ask Aura AI", path: "/market", protected: false },
        { text: "Pricing", path: "/pricing", protected: false },
        { text: "Founder's Note", path: "/founders", protected: false },
        { text: "FAQ", path: "/#faq", protected: false },
        { text: "Contact Us", path: "/contact-us", protected: false },
        {
          text: "Terms of Service",
          path: "/terms-and-conditions",
          protected: false,
        },
        { text: "Privacy Policy", path: "/privacy-policy", protected: false },
      ],
    },
  ];

  /* Campaign landing pages are bought traffic with one job, so the footer drops
     the column groups that lead away from them. The nav drops the same links
     (see Navbar), so the exits close together rather than one being left open.

     On the four INTERNATIONAL pages there is a second reason: remedies are
     India-only, so the shop and the India-centric Vedic calculators advertise
     things those visitors cannot use. */
  const FOCUSED_LANDINGS = [
    "/india",
    "/usa",
    "/uae",
    "/love",
    "/horoscope",
  ];
  const isFocusedLanding = FOCUSED_LANDINGS.includes(
    location.pathname.replace(/\/+$/, ""),
  );
  const visibleColumns = isFocusedLanding
    ? accordionData.filter(
        (c) => c.title !== "Free Calculators" && c.title !== "Astrology Blog",
      )
    : accordionData;

  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        <FooterEmailBanner />

        {!isMarket && (
          <>
            <header className={styles.header}>
              <h2 className={styles.title}>Explore Aura AI</h2>
              <p className={styles.subtitle}>
                Discover all our features and services
              </p>
            </header>

            <div className={styles.columnsWrapper}>
          <div className={styles.column}>
            <Link to="/" className={styles.logo}>
              {/* Explicit dimensions so the browser reserves the box before the
                  SVG loads. Source is 162x162, CSS renders it 70px high, so 1:1
                  is correct. Prevents a footer layout shift. */}
              <img
                src={logoSvg}
                alt="Aura AI Logo"
                className={styles.logoSvg}
                width={70}
                height={70}
              />
              <span>Aura AI</span>
            </Link>
            <p className={styles.byline}>by Stratnova Technologies LLP</p>
            <p className={styles.description}>
              Your trusted AI-powered astrology companion, providing
              personalized insights and guidance for your life journey.
            </p>
            <div className={styles.socials}>
              <a
                href="https://www.instagram.com/astroaura.ai?igsh=MXNxbmIzZG5vZjI0ag=="
                className={styles.socialIcon}
              >
                <IconBrandInstagram size={20} />
              </a>
              <a
                href="https://www.youtube.com/@AstroAura-ai
"
                className={styles.socialIcon}
              >
                <IconBrandYoutube size={20} />
              </a>
            </div>
          </div>

          {visibleColumns.map((col, index) => (
            <div
              className={`${styles.column} ${styles.accordionColumn}`}
              key={col.title}
            >
              <button
                className={styles.accordionHeader}
                onClick={() => toggleAccordion(index)}
              >
                <h3 className={styles.columnTitle}>{col.title}</h3>
                {openIndex === index ? (
                  <IconMinus size={20} />
                ) : (
                  <IconPlus size={20} />
                )}
              </button>

              <ul
                className={`${styles.linkList} ${openIndex === index ? styles.open : ""}`}
              >
                {col.links.map((link) => {
                  const isExternal =
                    link.path.startsWith("mailto:") ||
                    link.path.startsWith("http");
                  return (
                    <li key={link.text}>
                      {link.protected ? (
                        <a
                          href={(link as { seo?: string }).seo || link.path}
                          onClick={(e) => {
                            e.preventDefault();
                            handleProtectedLinkClick(link.path);
                          }}
                        >
                          {link.text}
                        </a>
                      ) : isExternal ? (
                        <a href={link.path}>{link.text}</a>
                      ) : (
                        <Link to={link.path}>{link.text}</Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

            <div className={styles.footerBottom}>
              <p>© {new Date().getFullYear()} Aura AI. All rights reserved.</p>

              <div className={styles.contactInfo}>
                <a
                  href="mailto:contact@astroaura.ai"
                  className={styles.emailLink}
                >
                  contact@astroaura.ai
                </a>
                {/* <span>+1 (555) 123-4567</span> */}
              </div>
            </div>
          </>
        )}
      </div>
    </footer>
  );
}
